/**
 * Optics tier paliers from game data (post-rescale GRU).
 * Source: fulda-data/GameData/Gameplay/Constantes/GDConstants.ndf — InterfaceSpecific
 * PaliersDOptiqueGRU, PaliersDOptiqueDAltitudeGRU
 */

/** Ground / standard / low-altitude optical strength GRU thresholds (ascending). */
export const GROUND_OPTICS_PALIER_KEYS = [0, 1768, 2651, 3535, 5301, 7068] as const;

/** High-altitude optical strength GRU thresholds (ascending). */
export const HIGH_ALTITUDE_OPTICS_PALIER_KEYS = [0, 5301, 10601, 13250] as const;

/** Lower bound of Exceptional tier (Value_6) for ground optics — division briefing / matchers. */
export const GROUND_OPTICS_EXCEPTIONAL_MIN =
  GROUND_OPTICS_PALIER_KEYS[GROUND_OPTICS_PALIER_KEYS.length - 1]!;

const GROUND_VALUE_TO_LABEL: Record<string, string> = {
  Value_1: 'Bad',
  Value_2: 'Mediocre',
  Value_3: 'Normal',
  Value_4: 'Good',
  Value_5: 'Very Good',
  Value_6: 'Exceptional',
};

/** High-altitude paliers only define Value_3 … Value_6; map to same UI labels as ground. */
const HIGH_ALT_VALUE_TO_LABEL: Record<string, string> = {
  Value_3: 'Normal',
  Value_4: 'Good',
  Value_5: 'Very Good',
  Value_6: 'Exceptional',
};

const GROUND_PALIERS: readonly (readonly [number, string])[] = [
  [0, 'Value_1'],
  [1768, 'Value_2'],
  [2651, 'Value_3'],
  [3535, 'Value_4'],
  [5301, 'Value_5'],
  [7068, 'Value_6'],
];

const HIGH_ALT_PALIERS: readonly (readonly [number, string])[] = [
  [0, 'Value_3'],
  [5301, 'Value_4'],
  [10601, 'Value_5'],
  [13250, 'Value_6'],
];

/** Pre-patch UI scale; still used when GRU is clearly legacy (below new-scale cutoff). */
const LEGACY_TIER_UPPER_BOUNDS = [42.45, 63.675, 84.9, 127.35, 191.025, 233.475] as const;

const LEGACY_TIER_LABELS = [
  'Bad',
  'Mediocre',
  'Normal',
  'Good',
  'Very Good',
  'Exceptional',
] as const;

/** Values below this are treated as pre-rescale GRU for display labels and for patch-diff scaling. */
export const GROUND_OPTICS_NEW_SCALE_MIN_GRU = 500;

/**
 * Uniform rescale from legacy optical strength GRU to current GRU (post-patch).
 * Patch diffs compare tiers in **new** palier space only: legacy values are multiplied by this first.
 * Calibrated from a representative row (70 → 2473). Update if Eugen rescales again.
 */
export const OPTICS_LEGACY_TO_NEW_GRU_SCALE = 2473 / 70;

/**
 * High-altitude GRU at or below this is treated as **pre-rescale** (legacy ~≤250; buffer to 300).
 * Current NDF still uses **small** post-rescale values for many ground units (e.g. 353, 706) and **8834** for SAMs;
 * those must **not** be multiplied by {@link OPTICS_LEGACY_TO_NEW_GRU_SCALE} (that factor is for ground optics only).
 */
export const AIR_OPTICS_LEGACY_MAX_GRU = 300;

function groundValueTokenForGru(gru: number): string {
  let token = GROUND_PALIERS[0]![1];
  for (const [bound, v] of GROUND_PALIERS) {
    if (gru >= bound) token = v;
  }
  return token;
}

function highAltValueTokenForGru(gru: number): string {
  let token = HIGH_ALT_PALIERS[0]![1];
  for (const [bound, v] of HIGH_ALT_PALIERS) {
    if (gru >= bound) token = v;
  }
  return token;
}

function legacyGroundTierIndex(gru: number): number {
  for (let i = 0; i < LEGACY_TIER_UPPER_BOUNDS.length; i++) {
    const cap = LEGACY_TIER_UPPER_BOUNDS[i]!;
    if (gru < cap) return i;
  }
  return LEGACY_TIER_LABELS.length - 1;
}

function legacyGroundTierLabel(gru: number): string {
  return LEGACY_TIER_LABELS[legacyGroundTierIndex(gru)]!;
}

function newGroundTierIndex(gru: number): number {
  const tok = groundValueTokenForGru(gru);
  const n = Number(tok.replace('Value_', ''));
  return Number.isFinite(n) ? n - 1 : 0;
}

/** Tier index 2…5 = Normal…Exceptional per `PaliersDOptiqueDAltitudeGRU` (Value_3…Value_6). */
function newHighAltTierIndex(gru: number): number {
  const tok = highAltValueTokenForGru(gru);
  const n = Number(tok.replace('Value_', ''));
  return Number.isFinite(n) ? n - 1 : 2;
}

/** Tier label for ground / low-alt optical strength GRU. */
export function getGroundOpticsTierLabel(gru: number): string {
  if (gru < GROUND_OPTICS_NEW_SCALE_MIN_GRU) {
    return legacyGroundTierLabel(gru);
  }
  const tok = groundValueTokenForGru(gru);
  return GROUND_VALUE_TO_LABEL[tok] ?? String(gru);
}

/** Tier label for high-altitude optical strength GRU. */
export function getHighAltitudeOpticsTierLabel(gru: number): string {
  if (gru <= AIR_OPTICS_LEGACY_MAX_GRU) {
    return legacyGroundTierLabel(gru);
  }
  const tok = highAltValueTokenForGru(gru);
  return HIGH_ALT_VALUE_TO_LABEL[tok] ?? String(gru);
}

/**
 * Stable 0–5 tier index for patch diff (ground / low-alt / air GRU).
 * Always buckets with **new** paliers. Legacy GRU is converted with {@link OPTICS_LEGACY_TO_NEW_GRU_SCALE}
 * so a pure rescale (e.g. 70 vs 2473) does not false-positive as a tier change.
 */
export function groundOpticsTierIndexForDiff(gru: number): number {
  const equivalentNew =
    gru < GROUND_OPTICS_NEW_SCALE_MIN_GRU
      ? Math.round(gru * OPTICS_LEGACY_TO_NEW_GRU_SCALE)
      : gru;
  return newGroundTierIndex(equivalentNew);
}

/**
 * Tier index for patch diff on **high-altitude** (`airOptics`) GRU (`PaliersDOptiqueDAltitudeGRU`).
 * Only values **≤ {@link AIR_OPTICS_LEGACY_MAX_GRU}** get the ground rescale factor — legacy high-alt topped out ~250.
 * Post-rescale NDF uses raw values such as **353** (many ground units) and **8834** (SAM); scaling those would false-flag tiers.
 * Indices 2–5 correspond to Normal…Exceptional on the air map (Value_3…Value_6).
 */
export function highAltitudeOpticsTierIndexForDiff(gru: number): number {
  const equivalentNew =
    gru <= AIR_OPTICS_LEGACY_MAX_GRU
      ? Math.round(gru * OPTICS_LEGACY_TO_NEW_GRU_SCALE)
      : gru;
  return newHighAltTierIndex(equivalentNew);
}

/** Map patch-diff tier index (0 = Bad … 5 = Exceptional) to a display label; `null` if not a valid index. */
export function opticsTierIndexToLabel(index: number): string | null {
  if (!Number.isInteger(index) || index < 0 || index >= LEGACY_TIER_LABELS.length) {
    return null;
  }
  return LEGACY_TIER_LABELS[index]!;
}
